import { useEffect, useState } from 'react';
import { Table, Card, Input, Select, Button, Tag, Typography, Space, Progress, Tooltip } from 'antd';
import { SearchOutlined, WarningOutlined, EyeOutlined, ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const { Title, Text } = Typography;
const fmt = (v) => Number(v || 0).toLocaleString('uz-UZ');

const RISK_COLOR = { high: '#f5222d', medium: '#fa8c16', low: '#52c41a' };
const RISK_LABEL = { high: 'Yuqori', medium: "O'rta", low: 'Past' };

export default function TaxAnalysisPage() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const fetchData = async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (search) params.search = search;
      const r = await api.get('/admingov/tax/vendor-analysis', { params });
      setData(r.data.data); setTotal(r.data.total);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { fetchData(1); }, []);
  useEffect(() => { fetchData(1); }, [search]);

  const exportCSV = () => {
    const headers = ['ID', 'Nomi', 'STIR', 'Egasi', 'Telefon', 'Buyurtmalar', 'Daromad', "To'lovlar", 'Risk (%)' , 'Risk daraja', 'Omillar'];
    const rows = data.map(r => [
      r.id, r.name, r.stir || '', r.owner_name, r.owner_phone,
      r.total_orders, r.total_order_amount, r.total_payments,
      r.risk_score, RISK_LABEL[r.risk_level], (r.risk_factors || []).join('; ')
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'soliq_tahlil.csv'; a.click();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 50 },
    { title: 'Biznes nomi', dataIndex: 'name', ellipsis: true },
    {
      title: 'STIR', dataIndex: 'stir', width: 120,
      render: v => v ? v : <Tag color="red">Mavjud emas</Tag>
    },
    { title: 'Egasi', dataIndex: 'owner_name', ellipsis: true },
    { title: 'Viloyat', dataIndex: 'region' },
    { title: 'Buyurtmalar', dataIndex: 'total_orders', width: 90, sorter: (a, b) => a.total_orders - b.total_orders },
    { title: 'Daromad', dataIndex: 'total_order_amount', render: v => fmt(v), sorter: (a, b) => a.total_order_amount - b.total_order_amount },
    { title: "To'lovlar", dataIndex: 'total_payments', render: v => fmt(v) },
    { title: 'Xodimlar', dataIndex: 'emp_count', width: 80 },
    { title: "To'y aloqasi", dataIndex: 'wedding_mentions', width: 90 },
    {
      title: 'Risk', dataIndex: 'risk_score', width: 120,
      sorter: (a, b) => a.risk_score - b.risk_score,
      defaultSortOrder: 'descend',
      render: (v, r) => (
        <Tooltip title={<div>{(r.risk_factors || []).map((f, i) => <div key={i}>- {f}</div>)}</div>}>
          <div>
            <Progress
              percent={v}
              size="small"
              strokeColor={RISK_COLOR[r.risk_level]}
              format={() => `${v}%`}
            />
            <Tag color={RISK_COLOR[r.risk_level]} style={{ fontSize: 10 }}>{RISK_LABEL[r.risk_level]}</Tag>
          </div>
        </Tooltip>
      ),
    },
    {
      title: '', width: 40,
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/admingov/tax/vendor/${r.id}`)} />
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          <WarningOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
          Soliq Tahlili — Vendor Profiling
        </Title>
        <Button icon={<ExportOutlined />} onClick={exportCSV}>CSV Export</Button>
      </div>

      <Card style={{ borderRadius: 12, marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="Biznes nomi yoki STIR" prefix={<SearchOutlined />} allowClear value={search} onChange={e => setSearch(e.target.value)} style={{ width: 300 }} />
          <Text type="secondary">
            Jami: <strong>{total}</strong> ta biznes tahlil qilinmoqda
          </Text>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12, marginBottom: 16, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)' }}>
        <Space>
          <WarningOutlined style={{ color: '#fa8c16', fontSize: 18 }} />
          <Text>
            Bu tizim xizmat ko'rsatuvchilarning barcha moliyaviy harakatlari, buyurtmalar, to'lovlar,
            xodimlar va to'y rejalashtirish moduli ma'lumotlarini tahlil qilib, <strong>soliqdan qochish ehtimolini</strong> prognoz qiladi.
            Risk ball qanchalik yuqori bo'lsa, shunchalik tekshirish kerak.
          </Text>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 1200 }}
          pagination={{ current: page, total, pageSize: 20, onChange: p => { setPage(p); fetchData(p); } }}
        />
      </Card>
    </div>
  );
}
